// File: src/components/Procurement/POItemViewer.jsx

import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Download,
  CalendarDays,
  Briefcase,
  Building2,
  User2,
  ClipboardList,
  Link2,
} from "lucide-react";
import api from "@/lib/axios";
import {Link} from "react-router-dom";
import {useUserPermissions} from "@/hooks/useUserPermissions";
import {verifyPoReviewSummary,formatPoReviewDay} from "@/lib/poFinancialReview";
import POFinancialReview from "./POFinancialReview";

const legacyPoNo = (poId) => `PO-${String(Number(poId) || 0).padStart(5, "0")}`;

const safeText = (value, fallback = "—") => {
  if (value === null || value === undefined) return fallback;
  const s = String(value).trim();
  return s ? s : fallback;
};

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return safeText(value);
  return d.toLocaleDateString();
};

const POItemViewer = ({ poId, expectedUser }) => {
  const {hasPermission}=useUserPermissions();
  const [loading, setLoading] = useState(true);
  const [header, setHeader] = useState(null);
  const [summaryItems, setSummaryItems] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError(""); setHeader(null); setSummaryItems([]);
    (async () => {
      try {
        if (!/^[1-9][0-9]*$/.test(String(poId))) throw new Error("The PO reference is invalid.");
        const {data} = await api.get(`/purchase-orders/${poId}/summary`, {
          signal: controller.signal, params: {_ts: Date.now()},
        });
        const value = verifyPoReviewSummary(data, expectedUser, poId);
        if (!controller.signal.aborted) {
          setHeader(value.header); setSummaryItems(value.items);
        }
      } catch (cause) {
        if (!controller.signal.aborted) setError(cause.message || "Failed to fetch purchase order details.");
      } finally { if (!controller.signal.aborted) setLoading(false); }
    })();
    return () => controller.abort();
  }, [poId, expectedUser]);

  const poNumber = useMemo(() => {
    return safeText(header?.po_number, legacyPoNo(poId));
  }, [header, poId]);

  const linkedEvent = useMemo(() => {
    const eventId = header?.event_id ?? null;
    const eventName = header?.event_name ?? null;
    const eventDate =
      header?.event_date ??
      header?.linked_event_date ??
      header?.event?.event_date ??
      null;

    return {
      eventId,
      eventName,
      eventDate,
      hasLinkedEvent: Boolean(eventId || eventName),
    };
  }, [header]);

  const handleExport = async () => {
    try {
      const res = await api.get(`/purchase-orders/pdf/${poId}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `PO_${poNumber || poId}.pdf`;
      link.click();
      window.URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error("PDF export failed:", err);
    }
  };

  return (
    <Card className="mt-4 border border-cyan-500/30 bg-[#0f1b3d] text-white shadow-xl">
      <CardContent className="p-6">
        <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white">{poNumber}</h2>
            <p className="mt-1 text-sm text-slate-300">Purchase order detail and approval context</p>
          </div>

          <div className="flex flex-wrap gap-3">
            {!loading && !error && header?.status === "Draft" && hasPermission("procurement.po.edit") && <Link
              to={`/procurement/new-po?draft_po_id=${header.po_id}`}
              className="rounded-lg bg-emerald-600 px-4 py-2 font-semibold text-white hover:bg-emerald-500"
            >Edit / submit draft</Link>}
          <Button
            onClick={handleExport}
            className="bg-blue-600 text-white hover:bg-blue-700"
            disabled={loading || Boolean(error) || !header}
          >
            <Download className="mr-2 h-4 w-4" />
            View / Export PDF
          </Button>
          </div>
        </div>

        {loading ? (
          <p className="text-cyan-300">Loading purchase order details...</p>
        ) : error ? (
          <p className="text-red-300">{error}</p>
        ) : (
          <>
            <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="mb-2 flex items-center gap-2 text-slate-400">
                  <Building2 className="h-4 w-4" />
                  <span className="text-sm">Vendor</span>
                </div>
                <p className="font-semibold text-white">{safeText(header?.vendor_name)}</p>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="mb-2 flex items-center gap-2 text-slate-400">
                  <User2 className="h-4 w-4" />
                  <span className="text-sm">Requested By</span>
                </div>
                <p className="font-semibold text-white">{safeText(header?.created_by_name)}</p>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="mb-2 flex items-center gap-2 text-slate-400">
                  <ClipboardList className="h-4 w-4" />
                  <span className="text-sm">Planner</span>
                </div>
                <p className="font-semibold text-white">{safeText(header?.planner_name)}</p>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="mb-2 flex items-center gap-2 text-slate-400">
                  <Briefcase className="h-4 w-4" />
                  <span className="text-sm">Status</span>
                </div>
                <p className="font-semibold text-white">{safeText(header?.status)}</p>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="mb-2 flex items-center gap-2 text-slate-400">
                  <CalendarDays className="h-4 w-4" />
                  <span className="text-sm">Expected Delivery</span>
                </div>
                <p className="font-semibold text-white">{formatPoReviewDay(header?.expected_delivery)}</p>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="mb-2 flex items-center gap-2 text-slate-400">
                  <Briefcase className="h-4 w-4" />
                  <span className="text-sm">Cost Centre</span>
                </div>
                <p className="font-semibold text-white">
                  {safeText(header?.cost_centre_name || header?.cost_centre_code)}
                </p>
              </div>

              {linkedEvent.hasLinkedEvent && (
                <>
                  <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4">
                    <div className="mb-2 flex items-center gap-2 text-emerald-300">
                      <Link2 className="h-4 w-4" />
                      <span className="text-sm">Linked Event</span>
                    </div>
                    <p className="font-semibold text-white">
                      {safeText(
                        linkedEvent.eventName,
                        linkedEvent.eventId ? `Event #${linkedEvent.eventId}` : "—"
                      )}
                    </p>
                  </div>

                  <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4">
                    <div className="mb-2 flex items-center gap-2 text-emerald-300">
                      <CalendarDays className="h-4 w-4" />
                      <span className="text-sm">Event Date</span>
                    </div>
                    <p className="font-semibold text-white">{formatDate(linkedEvent.eventDate)}</p>
                  </div>
                </>
              )}
            </div>

            <POFinancialReview summary={{header,items:summaryItems}} showReceiving />
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default POItemViewer;
